/**
 * createChatStore — the ONE chat-store factory (TASK-710, dock consolidation).
 *
 * Generalizes the proven workbenchChatStore pattern for every chat surface:
 * core `messages/isStreaming/error/conversationId`, named side-channel pending
 * slots (buffer-then-commit-on-`done` — the project convention), and optional
 * per-scope sessionStorage persistence.
 *
 * ## Scope identity (decision 2026-07-30)
 * A scope is entity-qualified, never type-only:
 * `{accountId}:{scopeType}:{entityId ?? 'global'}`. `kyc:<clientA>` and
 * `kyc:<clientB>` are different threads — a type-only key would carry one
 * client's conversation into another client's workspace. `accountId` is in the
 * key AND all dock scopes are purged on logout/account switch
 * (`purgeDockScopeStorage`) — sessionStorage survives auth changes in the same
 * tab, so the purge is load-bearing, not belt-and-braces.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// ============================================================================
// Scope identity
// ============================================================================

export type ChatScopeType =
  | 'dashboard'
  | 'kyc'
  | 'meeting'
  | 'workbench'
  | 'client'
  // TASK-769: the thread that follows a unit of work rather than a route.
  // Cross-client isolation is structural here: a task id belongs to exactly one
  // task, which belongs to exactly one client, so `task:<id>` cannot be shared.
  | 'task'
  | 'generic';

export interface ChatScope {
  scopeType: ChatScopeType;
  entityId?: string | null;
}

export const DOCK_STORAGE_PREFIX = 'kielis-dock:';

/** Entity-qualified sessionStorage key: `kielis-dock:{accountId}:{scopeType}:{entityId}`. */
export const dockScopeStorageKey = (accountId: string, scope: ChatScope): string =>
  `${DOCK_STORAGE_PREFIX}${accountId}:${scope.scopeType}:${scope.entityId ?? 'global'}`;

/**
 * Remove every persisted dock scope. Called on logout and account switch —
 * sessionStorage outlives the auth token in the same tab, and a surviving
 * thread would replay one login's conversation into the next.
 */
export const purgeDockScopeStorage = (): void => {
  const doomed: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(DOCK_STORAGE_PREFIX)) doomed.push(key);
  }
  doomed.forEach((key) => sessionStorage.removeItem(key));
};

// ============================================================================
// Store types
// ============================================================================

export interface ChatUIMessage<TSide extends SideChannels = SideChannels> {
  id: string;
  role: 'user' | 'assistant' | 'system';
  /** What went on the wire. Stays authoritative for history and resume. */
  content: string;
  /**
   * TASK-767: what the ADVISOR sees, when that differs from what was sent.
   * A delegated task carries routing scaffolding the model needs — the task
   * title first (the router classifies on it), the task/client ids, the
   * `work_task` instruction — none of which is something an advisor typed.
   * Render sites resolve `displayContent ?? content`; the payload is never
   * rewritten to make the bubble read better.
   */
  displayContent?: string;
  timestamp: string;
  /** Side-channel payloads attached on commit (proposals, edits, cards…). */
  side?: Partial<TSide>;
}

/**
 * TASK-767: a prompt one surface hands to another, split into what the model
 * receives and what the advisor is shown. A bare string means both — every
 * pre-existing caller (starter chips, Regenerate, revise) keeps working
 * unchanged.
 */
export interface PromptHandoff {
  display: string;
  payload: string;
}

export type RequestedPrompt = string | PromptHandoff;

export const resolvePrompt = (prompt: RequestedPrompt): PromptHandoff =>
  typeof prompt === 'string' ? { display: prompt, payload: prompt } : prompt;

/**
 * TASK-767's storage rule, in one place: `content` is always what went on the
 * wire, and `displayContent` exists ONLY when the two differ.
 *
 * TASK-790: the `...(display !== payload ? { displayContent: display } : {})`
 * spread was written out at three call sites (the dock's send and both of
 * InputDock's). Storing `displayContent` unconditionally would work too, but the
 * "only when it differs" rule is what keeps ordinary messages byte-identical in
 * sessionStorage — so it is a rule worth having one copy of.
 */
export const promptMessageContent = ({ display, payload }: PromptHandoff) => ({
  content: payload,
  ...(display !== payload ? { displayContent: display } : {}),
});

export type SideChannels = Record<string, unknown[]>;

/**
 * TASK-792 — what a turn is actually doing, as one value.
 *
 * ```
 * idle → routing → working → streaming → done | error
 *                     ↑                ↓
 *                     └──── blocked ───┘   (hard gate: awaiting the advisor)
 * ```
 *
 * Why this replaced a boolean: TASK-765 found that `isStreaming` was false for
 * the entire duration of every dock turn, and had been since the store was
 * written, because `setError` cleared it as a hidden side effect. Four
 * affordances were dead and nothing noticed. The boolean also stood beside a
 * SECOND flag — `wsStreaming` from `useAgenticChat` — and the two could
 * disagree: that is exactly why the rail degraded gracefully and the dock failed
 * outright from one root cause.
 *
 * Each state already existed implicitly and was unrepresentable: `routing` was
 * the invisible gap between send and first token that TASK-765 exposed,
 * `blocked` was an ad-hoc hard-gate confirm.
 *
 * TASK-797 removed `queued`. It was declared here and in `ACTIVE_TURN_STATES`
 * and **assigned nowhere** — a member no code could ever produce. The reason is
 * structural, not an oversight to correct later: "a prompt is waiting" is a fact
 * about the NEXT turn, while this field describes the one in flight, so a single
 * `turn` value cannot hold both. The queue it was named for is real and still
 * works — `useDockSend` holds `pendingPrompt` and sends it when `isStreaming`
 * clears — it simply is not a turn state. Every remaining member has both a
 * documented entry and a documented exit.
 */
export type TurnState =
  | 'idle'
  | 'routing'
  | 'working'
  | 'streaming'
  | 'done'
  | 'blocked'
  | 'error';

/**
 * The states in which a turn is in flight and the input must stay locked.
 *
 * TASK-797: `blocked` belongs here. It was omitted on the reading that a turn
 * awaiting a human decision is not "running" — but the turn IS still in flight,
 * and the human decision is part of it. Leaving it out unlocked the input and
 * hid the working strip from the moment the advisor answered the hard-gate
 * dialog until the final chunk arrived: a dead progress affordance plus a
 * firable second turn, which is exactly the failure TASK-765 fixed.
 *
 * It also made `blocked` a state with no exit — nothing calls `setTurn` on the
 * confirm/cancel handlers, and the transport promotion effect only lifts
 * `routing`/`working`. Membership here is the smaller of the two available
 * fixes and the one that cannot be forgotten by a future handler.
 */
const ACTIVE_TURN_STATES: ReadonlySet<TurnState> = new Set<TurnState>([
  'routing',
  'working',
  'streaming',
  'blocked',
]);

/** Single definition of "a turn is running", derived — never stored twice. */
export const isTurnActive = (turn: TurnState): boolean => ACTIVE_TURN_STATES.has(turn);

export interface ChatStoreState<TSide extends SideChannels> {
  conversationId: string | null;
  messages: ChatUIMessage<TSide>[];
  /**
   * TASK-792: derived from `turn`, never assigned independently. Kept as a field
   * so the ~40 existing read sites did not all have to change in the same commit
   * as the machine itself; `setTurn` is its only writer.
   */
  isStreaming: boolean;
  /** TASK-792: the authoritative turn state. */
  turn: TurnState;
  error: string | null;
  /** Buffered side-channel items per slot; committed onto the last assistant message. */
  pending: { [K in keyof TSide]: TSide[K] };

  setConversationId: (id: string) => void;
  addMessage: (message: ChatUIMessage<TSide>) => void;
  /**
   * TASK-792: the ONE writer of turn state. `isStreaming` moves with it.
   */
  setTurn: (turn: TurnState) => void;
  setError: (error: string | null) => void;
  addPending: <K extends keyof TSide>(slot: K, item: TSide[K][number]) => void;
  /** Attach all pending slots to the last assistant message; clear pending. */
  commitPending: () => void;
  reset: () => void;
}

export interface CreateChatStoreOptions<TSide extends SideChannels> {
  /** Devtools name. */
  name: string;
  /** Side-channel slot names (typed by TSide). */
  slots: Array<keyof TSide & string>;
  /**
   * When set, conversationId + messages persist to sessionStorage under this
   * key (use `dockScopeStorageKey`). Transients (streaming, pending, error)
   * are never persisted.
   */
  storageKey?: string;
  /** Max messages kept in storage (default 50). */
  persistLimit?: number;
  /**
   * TASK-719 persistence boundary: false = persist the conversationId ONLY
   * (no transcript/proposal content in sessionStorage). The KYC scope uses
   * this pending the security/product sign-off — the thread resumes via the
   * backend conversation store, content never touches browser storage.
   */
  persistMessages?: boolean;
}

export const generateChatMessageId = (prefix = 'chat_msg'): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

// ============================================================================
// Factory
// ============================================================================

export function createChatStore<TSide extends SideChannels>(
  options: CreateChatStoreOptions<TSide>
) {
  const { name, slots, storageKey, persistLimit = 50, persistMessages = true } = options;

  const emptyPending = () =>
    Object.fromEntries(slots.map((s) => [s, []])) as unknown as {
      [K in keyof TSide]: TSide[K];
    };

  const initialState = {
    conversationId: null as string | null,
    messages: [] as ChatUIMessage<TSide>[],
    isStreaming: false,
    turn: 'idle' as TurnState,
    error: null as string | null,
    pending: emptyPending(),
  };

  const definition = (set: (fn: unknown) => void) => ({
    ...initialState,

    setConversationId: (id: string) => set({ conversationId: id }),

    addMessage: (message: ChatUIMessage<TSide>) =>
      set((state: ChatStoreState<TSide>) => ({
        messages: [...state.messages, message],
        error: null,
      })),

    /**
     * TASK-792: the one place turn state changes. `isStreaming` is written here
     * and NOWHERE else, so it cannot drift from `turn` and no action can clear
     * it as a side effect — the shape of the TASK-765 defect.
     */
    setTurn: (turn: TurnState) => set({ turn, isStreaming: isTurnActive(turn) }),

    /**
     * TASK-765: sets `error` ONLY. It used to also force `isStreaming: false`,
     * which made `setError(null)` — the standard "clear the previous error"
     * call at the top of a send — destroy the streaming guard one line after
     * it was set. Stopping a turn is `setTurn('done'|'error')`, explicitly, at the
     * places that mean it.
     */
    setError: (error: string | null) => set({ error }),

    addPending: <K extends keyof TSide>(slot: K, item: TSide[K][number]) =>
      set((state: ChatStoreState<TSide>) => ({
        pending: { ...state.pending, [slot]: [...state.pending[slot], item] },
      })),

    commitPending: () =>
      set((state: ChatStoreState<TSide>) => {
        const filled = slots.filter((s) => state.pending[s].length > 0);
        if (filled.length === 0 || state.messages.length === 0) return state;

        const messages = [...state.messages];
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === 'assistant') {
            const side = { ...(messages[i].side ?? {}) } as Partial<TSide>;
            for (const s of filled) side[s] = state.pending[s];
            messages[i] = { ...messages[i], side };
            return { messages, pending: emptyPending() };
          }
        }
        return state; // no assistant message yet — keep buffering
      }),

    reset: () => set({ ...initialState, pending: emptyPending() }),
  });

  if (!storageKey) {
    return create<ChatStoreState<TSide>>()(
      devtools(definition as never, { name })
    );
  }

  return create<ChatStoreState<TSide>>()(
    devtools(
      persist(definition as never, {
        name: storageKey,
        storage: {
          getItem: (key: string) => {
            const str = sessionStorage.getItem(key);
            if (!str) return null;
            try {
              return JSON.parse(str);
            } catch {
              // Corrupt/older-schema payloads start fresh rather than crash.
              return null;
            }
          },
          setItem: (key: string, value: unknown) => {
            sessionStorage.setItem(key, JSON.stringify(value));
          },
          removeItem: (key: string) => sessionStorage.removeItem(key),
        },
        partialize: (state: ChatStoreState<TSide>) =>
          ({
            conversationId: state.conversationId,
            ...(persistMessages ? { messages: state.messages.slice(-persistLimit) } : {}),
          }) as never,
      } as never),
      { name }
    )
  );
}
